import React, { useState } from 'react';
import { Input, Modal } from 'antd';
import { useMutation } from '@tanstack/react-query';
import './LegendDrawer.scss';
import MapPortalService from '../../../services/MapPortal/mapportal.service';
import { useAuth } from '../../../context/AuthContext';
import { LayerAction } from '../../../enums/mapPortalActions';
import { layerActionAccess } from '../constants';
import { DUPLICATE_GROUP_NAME_ERROR } from '../../CommonMap/constants';
import type { DrawerItem, LayerGroup } from './LegendDrawer';

interface CreateGroupModalProps {
  mapId: number;
  // The layer to move into the new group; the modal is open while it is set.
  layer: Pick<DrawerItem, 'id' | 'layerTitle' | 'uniqueLayerName'> | null;
  groups: LayerGroup[];
  onClose: () => void;
  onCreated: () => Promise<void>;
}

/** Create Group modal for the legend's layer options. Shared by Map Portal's legend and CommonMap. */
const CreateGroupModal: React.FC<CreateGroupModalProps> = ({ mapId, layer, groups, onClose, onCreated }) => {
  const [newGroupName, setNewGroupName] = useState<string>('');
  const [groupNameError, setGroupNameError] = useState<string>('');
  const [isGroupSaving, setIsGroupSaving] = useState(false);
  const { canAccess } = useAuth();
  const canManageGroups = canAccess(layerActionAccess(LayerAction.MANAGE_GROUPS));
  const { createLayerGroup } = MapPortalService();
  const { mutateAsync: createLayerGroupAsync } = useMutation(createLayerGroup());

  const isDuplicateGroupName = (name: string) => groups.some((g) => g.name.trim().toLowerCase() === name.trim().toLowerCase());

  const close = () => {
    setNewGroupName('');
    setGroupNameError('');
    onClose();
  };

  const handleConfirmCreateGroup = async () => {
    // Also commits on Enter, not just the OK button.
    if (!canManageGroups) return;
    const name = newGroupName.trim();
    if (!layer || !name) return;
    if (isDuplicateGroupName(name)) {
      setGroupNameError(DUPLICATE_GROUP_NAME_ERROR);
      return;
    }
    setIsGroupSaving(true);
    try {
      await createLayerGroupAsync({
        mapId,
        name,
        layerId: layer.id,
      });
      await onCreated();
      close();
    } catch (error) {
      // Backend is source of truth (race / multi-tab). Keep modal open with inline error.
      const axiosError = error as { response?: { data?: { message?: string; error?: { message?: string } }; status?: number } };
      const serverMessage = axiosError?.response?.data?.error?.message ?? axiosError?.response?.data?.message;
      if (axiosError?.response?.status === 409 || serverMessage) {
        setGroupNameError(serverMessage || DUPLICATE_GROUP_NAME_ERROR);
      }
    } finally {
      setIsGroupSaving(false);
    }
  };

  return (
    <Modal
      open={!!layer}
      title="Create Group"
      okText="Create"
      okButtonProps={{ disabled: !newGroupName.trim(), loading: isGroupSaving }}
      onOk={handleConfirmCreateGroup}
      onCancel={close}
      maskClosable={false}
      destroyOnClose
    >
      <p className="legend-create-group__hint">
        Enter a name for the new group. The layer
        {layer ? ` "${layer.layerTitle || layer.uniqueLayerName}"` : ''} will be moved into it.
      </p>
      <Input
        autoFocus
        placeholder="Group name"
        value={newGroupName}
        status={groupNameError ? 'error' : ''}
        onChange={(e) => {
          setNewGroupName(e.target.value);
          if (groupNameError) setGroupNameError('');
        }}
        onPressEnter={handleConfirmCreateGroup}
      />
      {groupNameError && <div className="legend-create-group__error">{groupNameError}</div>}
    </Modal>
  );
};

export default CreateGroupModal;
